/**
 * @file   src\pages\Settings.js
 * @brief  This file is responsible for handling account settings.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useMemo } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUserDetails } from "../actions/userprofile";
import { getUserDetails } from "../services/userprofileService";
import { NOT_FREE_OR_MEMBER_PERIOD, USER_INFO_KEY } from "../constants/common";
import { getLocalStorage } from "../helpers/common";
import ProfileNormalUser from "./ProfileNormalUser";
import ProfileAgentUser from "./ProfileAgentUser";
import ProfileAgentUserPremium from "./v2/ProfileAgentUserPremium";
import Notifications from "./Notifications";
import Payments from "./Payments";
import Loader from "../components/Common/Loader";
import { useLocation } from "react-router-dom";
import { PageWrapper } from "@/components/PageWrapper";

const USER_DETAILS_KEY = "userDetails";

const Settings = (props) => {

  const { addNewCard, editAgentProfile } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const userId = _.get(sessionData, "userInfo.userId", "");
  const location = useLocation();
  const isFromNotification =
    location.state && location.state["isFromNotification"];
  const [key, setKey] = useState(addNewCard ? "payments" : "account");

  const userDetailsQuery = useQuery({
    queryKey: [USER_DETAILS_KEY],
    queryFn: async () => {
      const response = await getUserDetails();
      // Mirror into Redux so the shared Basic/Premium plan modals rendered under
      // this page, which read state.userProfile.userDetails, keep working
      // without issuing a second network request.
      dispatch(fetchUserDetails.fulfilled(response, "rq-mirror"));
      return response;
    },
    enabled: !!userId,
    // Settings is where plan/profile changes land, so always re-read on mount
    // instead of serving the globally configured 5 minute stale window.
    staleTime: 0,
  });

  const userPlans = useMemo(
    () => _.get(userDetailsQuery.data, "result", null),
    [userDetailsQuery.data],
  );

  const userDetails = userPlans?.length > 0 ? userPlans[0] : null;
  const upComingPlanDetails =
    userPlans?.length > 1 ? userPlans[userPlans.length - 1] : null;
  const userType = userDetails?.UserTypeId;

  const updateReloadParam = () => {
    queryClient.invalidateQueries({ queryKey: [USER_DETAILS_KEY] });
  };

  const renderAccountTab = () => {
    if (userDetailsQuery.isLoading) {
      return <Loader />;
    }

    if (userDetailsQuery.isError) {
      return (
        <div className="text-center pt-4">
          Unable to load your account details. Please refresh the page and try
          again.
        </div>
      );
    }

    if (userType === 1) {
      return (
        <ProfileNormalUser
          userDetails={userDetails}
          updateReloadParam={updateReloadParam}
          upComingPlanDetails={upComingPlanDetails}
          isFromNotification={isFromNotification}
        />
      );
    }

    if (userType === 2 && userDetails?.period > NOT_FREE_OR_MEMBER_PERIOD) {
      return (
        <ProfileAgentUserPremium
          userDetails={userDetails}
          updateReloadParam={updateReloadParam}
          upComingPlanDetails={upComingPlanDetails}
          isFromNotification={isFromNotification}
          editAgentProfile={editAgentProfile}
        />
      );
    }

    return (
      <ProfileAgentUser
        userDetails={userDetails}
        updateReloadParam={updateReloadParam}
        upComingPlanDetails={upComingPlanDetails}
        isFromNotification={isFromNotification}
        editAgentProfile={editAgentProfile}
      />
    );
  };

  return (
    <>
      <PageWrapper>
        <div className="m-0 pb-4">
          <div className="main-content p-0 agent-no-login-content">
            <div className="row m-0">
              <div className="col-md-12">
                <div className="edit-want-ad-wrap settings-wrap mw-626">
                  <h3 className="heading mb-20">Settings</h3>

                  <div className="horizontal-tab-wrap">
                    <Tabs
                      id="controlled-tab-example1"
                      className="mb-0"
                      activeKey={key}
                      onSelect={(k) => {
                        setKey(k);
                      }}
                      fill
                    >
                      <Tab eventKey="account" title="Account">
                        {renderAccountTab()}
                      </Tab>

                      <Tab eventKey="payments" title="Payments">
                        <Payments currentTab={key} addNewCard={addNewCard} />
                      </Tab>

                      <Tab eventKey="notifications" title="Notifications">
                        <Notifications userDetails={userDetails} />
                      </Tab>
                    </Tabs>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default Settings;
